import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";

const LoadStateContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
  padding: 16,
}));

type SessionPermissionsLoadStateProps = {
  error: string | null;
  onRetry: () => void;
};

/**
 * What a page shows while it does not yet know what the reader may see.
 *
 * Every page in the application gates its content on the session permissions, and each one used to
 * render a bare loader while they were absent. That conflated two states: still arriving, and failed
 * to arrive. A failed lookup left the loader up forever, and the `NotAuthorized` branch written one
 * line below the gate could never be reached — so a reader who should have been refused waited
 * instead, with nothing in flight and no error anywhere on screen.
 *
 * Distinguishing them is the whole job. A retry is offered because the failure is usually transport,
 * and re-asking is the one thing the reader can do about it.
 */
export default function SessionPermissionsLoadState({
  error,
  onRetry,
}: Readonly<SessionPermissionsLoadStateProps>) {
  if (!error) {
    return (
      <LoadStateContainer>
        <Loader fontSize="20px" text="Loading permissions" />
      </LoadStateContainer>
    );
  }

  return (
    <LoadStateContainer>
      <Alert
        action={
          <Button color="inherit" onClick={onRetry} size="small">
            Retry
          </Button>
        }
        severity="error"
      >
        Unable to load your access permissions. {error}
      </Alert>
    </LoadStateContainer>
  );
}
